/**
 * Stable analytics identity + device context.
 *
 * Every analytics event carries:
 *  - `session_id`  — one id per browser tab session (30-minute idle window),
 *    so a visit can be followed end-to-end across pages.
 *  - `visitor_id`  — long-lived id in localStorage, so returning visitors can
 *    be told apart from first-time visitors.
 *  - device / browser / viewport, so mobile-vs-desktop drop-off is visible.
 *
 * All of it is best-effort and never throws: private mode, blocked storage
 * and SSR all degrade to a memory-only id.
 */

const SESSION_KEY = "rt_analytics_session";
const VISITOR_KEY = "rt_analytics_visitor";
const IDLE_MS = 30 * 60 * 1000;

let memorySession: string | null = null;

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function readRaw(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeRaw(store: Storage | undefined, key: string, value: string) {
  try {
    store?.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

/** Per-visit session id. Rolls over after 30 minutes of inactivity. */
export function getSessionId(): string | null {
  if (typeof window === "undefined") return null;
  const now = Date.now();
  const raw = readRaw(window.localStorage, SESSION_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { id: string; last: number };
      if (parsed.id && now - parsed.last < IDLE_MS) {
        writeRaw(window.localStorage, SESSION_KEY, JSON.stringify({ id: parsed.id, last: now }));
        memorySession = parsed.id;
        return parsed.id;
      }
    } catch {
      /* fall through and mint a new one */
    }
  }
  const id = memorySession && !raw ? memorySession : newId();
  memorySession = id;
  writeRaw(window.localStorage, SESSION_KEY, JSON.stringify({ id, last: now }));
  return id;
}

/** Long-lived visitor id used to separate new from returning visitors. */
export function getVisitorId(): string | null {
  if (typeof window === "undefined") return null;
  const existing = readRaw(window.localStorage, VISITOR_KEY);
  if (existing) return existing;
  const id = newId();
  writeRaw(window.localStorage, VISITOR_KEY, id);
  return id;
}

export function isReturningVisitor(): boolean {
  if (typeof window === "undefined") return false;
  return !!readRaw(window.localStorage, VISITOR_KEY);
}

export type DeviceContext = {
  device_type: "mobile" | "tablet" | "desktop";
  os: string;
  browser: string;
  /** True inside Instagram / Facebook / TikTok in-app browsers. */
  in_app_browser: boolean;
  viewport_w: number | null;
  viewport_h: number | null;
};

export function getDeviceContext(): DeviceContext | null {
  if (typeof window === "undefined") return null;
  const ua = navigator.userAgent || "";
  const isTablet = /iPad/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua));
  const isMobile = !isTablet && /iPhone|iPod|Android|Mobi/i.test(ua);

  let os = "other";
  if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Mac OS X/i.test(ua)) os = "macOS";
  else if (/Windows/i.test(ua)) os = "Windows";
  else if (/Linux/i.test(ua)) os = "Linux";

  const inApp = /Instagram|FBAN|FBAV|FB_IAB|TikTok|Musical_ly|Snapchat|LinkedInApp|Pinterest/i.test(ua);
  let browser = "other";
  if (/Instagram/i.test(ua)) browser = "Instagram in-app";
  else if (/FBAN|FBAV|FB_IAB/i.test(ua)) browser = "Facebook in-app";
  else if (/TikTok|Musical_ly/i.test(ua)) browser = "TikTok in-app";
  else if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR\//i.test(ua)) browser = "Opera";
  else if (/Chrome\//i.test(ua)) browser = "Chrome";
  else if (/Firefox\//i.test(ua)) browser = "Firefox";
  else if (/Safari\//i.test(ua)) browser = "Safari";

  return {
    device_type: isTablet ? "tablet" : isMobile ? "mobile" : "desktop",
    os,
    browser,
    in_app_browser: inApp,
    viewport_w: window.innerWidth || null,
    viewport_h: window.innerHeight || null,
  };
}
